/**
 * A 词典 reading, one syllable at a time, with the tone each is read in.
 *
 * `{4rèn}{5shi}` is 认识. Three indexes hold a reading in this encoding now:
 * the 例句 index it came from (`src/site-search/liju-row.ts`), the published
 * suggestion file (`./cidian-suggestion-line.ts`) and the search's own 词典
 * index (`src/site-search/cidian-row.ts`). This is the one place that writes
 * it and reads it back.
 *
 * **The syllables come off the entry's own front matter.** `pnpm run cidian`
 * writes one tone-coloured span per syllable, which is what
 * `layouts/cidian/summary.html` draws the reading from, and
 * {@link readingSyllables} reads the tones and the boundaries straight out of
 * it. The alternative is segmenting the reading back out of `bùān` with a
 * syllable table, and that is what this replaced: `readWord` is greedy, so it
 * reads `bùān` as one syllable spelled buān and 不安 was indexed under a
 * reading nobody says. Thirty-one entries were wrong that way, 十二, 图案 and
 * 第二 among them.
 *
 * **The tone is a digit and the text keeps its marks.** A ranking wants the
 * tone on its own, 情 and 清 differing in nothing else, and a page wants
 * `qíng` as the section spells it. Holding both is what lets a result row
 * draw the reading in tone colours without a second reading of the reading.
 *
 * Nothing here loads `@kensio/pinyinjs`. The module is bundled into a browser
 * as well as into the handler, and the package carries a syllable table that
 * neither end needs once the build has done the segmenting.
 */

/**
 * The neutral tone, as the site's own pinyin markup numbers it.
 *
 * `NEUTRAL_TONE` in `@kensio/pinyinjs`, written out here for the reason above.
 */
export const neutralTone = 5;

/** The tone of a syllable whose markup named none, which nothing matches. */
export const untoned = 0;

/** One syllable of a reading, as an index holds it. */
export interface CidianSyllable {
  /** The tone, 1 to 4, {@link neutralTone}, or {@link untoned}. */
  readonly tone: number;

  /** The syllable as a page draws it, tone mark and all. */
  readonly text: string;
}

/**
 * One syllable of a published reading, as an entry's front matter writes it.
 *
 * The class list is read rather than matched whole. What the section writes is
 * `class="py-syllable py-tone-2" lang="zh-Latn-CN-pinyin"`, and what this needs
 * out of it is the tone, so a span that carries the tone class and nothing else
 * is read the same way. `src/content/liju/liju-index-line.ts` matches the whole
 * attribute because it is rebuilding the span rather than reading it.
 */
const readingSpan = /<span\b[^>]*class="([^"]*)"[^>]*>([^<]*)<\/span>/gu;

/** The tone a syllable's class list names, where it names one. */
const toneClass = /py-tone-(\d)/u;

/** One syllable of a reading, as an index encodes it. */
const encodedSyllable = /\{(\d)([^}]*)\}/gu;

/**
 * What no field of an index line can carry, and what to call it.
 *
 * A tab or a newline would split one line into the wrong columns or into two
 * lines, and the index at the far end would hold whatever came out.
 */
const unwritable = new Map([
  ["\t", "a tab"],
  ["\n", "a newline"],
]);

/**
 * What a reading cannot carry on top of those.
 *
 * The braces are the encoding's, so one inside a syllable would be read back
 * as the end of it and take the rest of the reading with it. Only a reading is
 * refused them. CC-CEDICT defines 不了 as `(pattern: {verb} + 個 ...)`, nothing
 * ever decodes the glosses column, and a definition is not the index's to
 * rewrite.
 */
const unreadable = new Map([
  ...unwritable,
  ["{", "an opening brace"],
  ["}", "a closing brace"],
]);

/** Why a word could not be written into an index. */
export class CidianIndexLineError extends Error {}

/**
 * Refuse a field an index could not carry, naming the word and the cause.
 *
 * Both the search index and the published suggestion file are lines of
 * separated columns, so both refuse the same characters about the same words.
 * `cidian-search-line.ts` and `cidian-suggestion-line.ts` are the two.
 */
export function refuseUnwritable(word: string, field: string): void {
  refuse(unwritable, word, field);
}

/** The same of a reading, which carries the encoding's braces as well. */
export function refuseUnreadable(word: string, reading: string): void {
  refuse(unreadable, word, reading);
}

/** Refuse a field holding any of them, naming the first one it holds. */
function refuse(
  refused: ReadonlyMap<string, string>,
  word: string,
  field: string,
): void {
  for (const [character, described] of refused) {
    if (field.includes(character)) {
      throw new CidianIndexLineError(
        `${word} has ${described} in it, which a 词典 index cannot carry. ` +
          "See src/content/cidian/cidian-syllables.ts.",
      );
    }
  }
}

/**
 * The reading as one string, for anything that wants it whole.
 *
 * The syllables written out in order and nothing between them, which is how
 * every reading in the section is written: `qíngkuàng`, never `qíng kuàng`.
 */
export function syllablesText(syllables: readonly CidianSyllable[]): string {
  return syllables.map((syllable) => syllable.text).join("");
}

/**
 * The syllables of a reading, out of the markup an entry's front matter holds.
 *
 * An entry whose front matter carries no markup is read as one untoned
 * syllable. That indexes the word under its letters without claiming a tone
 * nothing wrote, where reading no syllables out of it would drop the reading.
 * Every entry in the section carries the markup.
 */
export function readingSyllables(
  html: string | undefined,
  text: string,
): readonly CidianSyllable[] {
  if (html === undefined || html === "") {
    return [{ tone: untoned, text }];
  }

  /*
   * Both groups of a match are there, neither being optional in the pattern,
   * which is what the assertions say. The tone class is the one that can be
   * absent: a span the theme drew with no tone takes {@link untoned}.
   */
  return [...html.matchAll(readingSpan)].map((found) => ({
    tone: Number(toneClass.exec(found[1]!)?.[1] ?? untoned),
    text: found[2]!,
  }));
}

/** One reading, as the syllables and tones an index carries it as. */
export function encodeSyllables(syllables: readonly CidianSyllable[]): string {
  return syllables
    .map((syllable) => `{${String(syllable.tone)}${syllable.text}}`)
    .join("");
}

/**
 * One reading, back out of the encoding.
 *
 * Anything outside a brace is text nobody gave a tone, which is what a
 * truncated file leaves behind and what a reading written by hand could carry.
 * It comes back as an untoned syllable rather than being dropped, so the word
 * is still findable by its letters.
 */
export function decodeSyllables(reading: string): readonly CidianSyllable[] {
  const syllables: CidianSyllable[] = [];
  let at = 0;

  for (const found of reading.matchAll(encodedSyllable)) {
    if (found.index > at) {
      syllables.push({ tone: untoned, text: reading.slice(at, found.index) });
    }

    syllables.push({ tone: Number(found[1]), text: found[2]! });
    at = found.index + found[0].length;
  }

  if (at < reading.length) {
    syllables.push({ tone: untoned, text: reading.slice(at) });
  }

  return syllables;
}
